/* eslint-disable react/jsx-max-props-per-line */
/* eslint-disable react/jsx-sort-props */
import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Grid,
  Skeleton,
  ToggleButton,
  ToggleButtonGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import CompareArrowsOutlinedIcon from '@mui/icons-material/CompareArrowsOutlined';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import LeaderboardOutlinedIcon from '@mui/icons-material/LeaderboardOutlined';
import { StatCardV2 } from '../../../components/v2';
import ChartFocusDialog from '../RelatorioDigitacao/ChartFocusDialog';
import {
  computeParetoInsight,
  formatBRL,
  formatPercent,
  getTopRanking,
  getRankingSortValue,
  RANKING_SORT_OPTIONS,
  RANKING_TOP_N,
  sortRankingBy,
} from './constants';
import { ChartCard, FOCUS_CHART_HEIGHT } from './chartShared';
import SegmentComparisonChart from './SegmentComparisonChart';
import SegmentComparisonTable from './SegmentComparisonTable';
import VeiculoRankingChart from './VeiculoRankingChart';

const PODIUM_COLORS = ['primary', 'default', 'default'];
const PODIUM_LABELS = ['#1', '#2', '#3'];

const RankingSortToggle = ({ sortKey, onChange }) => {
  const handleChange = (_event, next) => {
    if (!next) return;
    onChange?.(next);
  };

  const getToggleLabel = (opt) => {
    if (opt.key === 'combustivel') return 'Comb.';
    if (opt.key === 'manutencao') return 'Manut.';
    if (opt.key === 'pecas') return 'Peças';
    if (opt.key === 'multas') return 'Multas';
    if (opt.key === 'ipva') return 'IPVA';
    return 'Total';
  };

  return (
    <Stack direction="row" alignItems="center" spacing={2} sx={{ flexWrap: 'wrap' }}>
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
        Ordenar por maior
      </Typography>
      <ToggleButtonGroup
        value={sortKey}
        exclusive
        size="small"
        onChange={handleChange}
        aria-label="Ordenação do ranking"
        sx={{ flexWrap: 'wrap' }}
      >
        {RANKING_SORT_OPTIONS.map((opt) => (
          <ToggleButton
            key={opt.key}
            value={opt.key}
            sx={{ textTransform: 'none', px: 1.5 }}
          >
            {getToggleLabel(opt)}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Stack>
  );
};

RankingSortToggle.propTypes = {
  sortKey: PropTypes.string,
  onChange: PropTypes.func,
};

RankingSortToggle.defaultProps = {
  sortKey: 'total',
  onChange: null,
};

const ParetoInsight = ({ insight, loading, entityLabel, sortKey, sortLabel }) => {
  if (loading) {
    return <Skeleton variant="rounded" height={72} sx={{ borderRadius: 2 }} />;
  }

  const { topCount, topPercent, totalVehicles, totalGasto } = insight;

  if (totalVehicles === 0) return null;

  const entityWord = topCount === 1 ? entityLabel.singular : entityLabel.plural;
  const fleetWord = totalVehicles === 1 ? entityLabel.singular : entityLabel.plural;

  const metricPhrase = sortKey === 'total' ? 'custo total' : `custo de ${sortLabel}`;
  return (
    <Alert
      severity="info"
      variant="outlined"
      icon={<InsightsOutlinedIcon />}
      sx={{ borderRadius: 2, alignItems: 'flex-start' }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        Top {topCount} {entityWord} concentram {formatPercent(topPercent)} do {metricPhrase}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
        {formatBRL(totalGasto)} em {totalVehicles} {fleetWord} no período
        {entityLabel.hint ? ` — ${entityLabel.hint}` : ''}
      </Typography>
    </Alert>
  );
};

ParetoInsight.propTypes = {
  insight: PropTypes.shape({
    topCount: PropTypes.number,
    topPercent: PropTypes.number,
    totalVehicles: PropTypes.number,
    totalGasto: PropTypes.number,
  }).isRequired,
  loading: PropTypes.bool,
  entityLabel: PropTypes.shape({
    singular: PropTypes.string,
    plural: PropTypes.string,
    hint: PropTypes.string,
  }),
  sortKey: PropTypes.string,
  sortLabel: PropTypes.string,
};

ParetoInsight.defaultProps = {
  loading: false,
  entityLabel: {
    singular: 'veículo',
    plural: 'veículos',
    hint: 'clique em um veículo para ver o detalhamento',
  },
  sortKey: 'total',
  sortLabel: 'Total',
};

const Top3Podium = ({
  ranking,
  metricTotal,
  sortKey,
  sortLabel,
  loading,
  onSelectPlaca,
}) => {
  const top3 = useMemo(
    () => (ranking || []).filter((row) => getRankingSortValue(row, sortKey) > 0).slice(0, 3),
    [ranking, sortKey],
  );

  if (loading) {
    return (
      <Grid container spacing={2}>
        {[0, 1, 2].map((i) => (
          <Grid key={i} size={{ xs: 12, sm: 4 }}>
            <Skeleton variant="rounded" height={100} />
          </Grid>
        ))}
      </Grid>
    );
  }

  if (top3.length === 0) return null;

  return (
    <Grid container spacing={2}>
      {top3.map((item, index) => {
        const value = getRankingSortValue(item, sortKey);
        const percent = metricTotal > 0 ? (value / metricTotal) * 100 : 0;
        const metricPhrase = sortKey === 'total' ? 'custo total' : `custo de ${sortLabel}`;
        return (
          <Grid key={item.placa} size={{ xs: 12, sm: 4 }}>
            <StatCardV2
              label={`${PODIUM_LABELS[index]} · ${item.placa}`}
              value={formatBRL(value)}
              icon={index === 0 ? <EmojiEventsIcon /> : <LeaderboardOutlinedIcon />}
              color={PODIUM_COLORS[index]}
              valueFontSize="1.25rem"
              onClick={() => onSelectPlaca?.(item.placa, item.veiculoId)}
            />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 0.5, pl: 0.5 }}
            >
              {formatPercent(percent)} do {metricPhrase}
            </Typography>
          </Grid>
        );
      })}
    </Grid>
  );
};

Top3Podium.propTypes = {
  ranking: PropTypes.arrayOf(PropTypes.object),
  metricTotal: PropTypes.number,
  sortKey: PropTypes.string,
  sortLabel: PropTypes.string,
  loading: PropTypes.bool,
  onSelectPlaca: PropTypes.func,
};

Top3Podium.defaultProps = {
  ranking: [],
  metricTotal: 0,
  sortKey: 'total',
  sortLabel: 'Total',
  loading: false,
  onSelectPlaca: null,
};

const RankingTable = ({
  ranking,
  metricTotal,
  sortKey,
  loading,
  onSelectPlaca,
}) => {
  const rows = useMemo(
    () => getTopRanking(ranking, RANKING_TOP_N, sortKey),
    [ranking, sortKey],
  );

  if (loading) {
    return <Skeleton variant="rounded" height={280} />;
  }

  if (rows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
        Sem veículos com custo no período.
      </Typography>
    );
  }

  return (
    <TableContainer sx={{ maxHeight: 360 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600, width: 40 }}>#</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Placa</TableCell>
            <TableCell
              align="right"
              sx={{ fontWeight: sortKey === 'total' ? 800 : 600 }}
            >
              Total
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 600 }}>%</TableCell>
            <TableCell
              align="right"
              sx={{
                fontWeight: sortKey === 'combustivel' ? 800 : 600,
                display: { xs: 'none', md: 'table-cell' },
              }}
            >
              Comb.
            </TableCell>
            <TableCell
              align="right"
              sx={{
                fontWeight: sortKey === 'manutencao' ? 800 : 600,
                display: { xs: 'none', md: 'table-cell' },
              }}
            >
              Manut.
            </TableCell>
            <TableCell
              align="right"
              sx={{ fontWeight: sortKey === 'pecas' ? 800 : 600, display: { xs: 'none', lg: 'table-cell' } }}
            >
              Peças
            </TableCell>
            <TableCell
              align="right"
              sx={{ fontWeight: sortKey === 'multas' ? 800 : 600, display: { xs: 'none', lg: 'table-cell' } }}
            >
              Multas
            </TableCell>
            <TableCell
              align="right"
              sx={{ fontWeight: sortKey === 'ipva' ? 800 : 600, display: { xs: 'none', lg: 'table-cell' } }}
            >
              IPVA
            </TableCell>
            <TableCell
              align="right"
              sx={{ fontWeight: sortKey === 'outros' ? 800 : 600, display: { xs: 'none', lg: 'table-cell' } }}
            >
              Outros
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, index) => {
            const value = getRankingSortValue(row, sortKey);
            const percent = metricTotal > 0 ? (value / metricTotal) * 100 : 0;
            return (
              <TableRow
                key={row.placa}
                hover
                onClick={() => onSelectPlaca?.(row.placa, row.veiculoId)}
                sx={{ cursor: onSelectPlaca ? 'pointer' : 'default' }}
              >
                <TableCell>{index + 1}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{row.placa}</TableCell>
                <TableCell
                  align="right"
                  sx={{ fontWeight: sortKey === 'total' ? 700 : undefined }}
                >
                  {formatBRL(row.total)}
                </TableCell>
                <TableCell align="right">{formatPercent(percent)}</TableCell>
                <TableCell
                  align="right"
                  sx={{
                    display: { xs: 'none', md: 'table-cell' },
                    fontWeight: sortKey === 'combustivel' ? 700 : undefined,
                  }}
                >
                  {formatBRL(row.combustivel)}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    display: { xs: 'none', md: 'table-cell' },
                    fontWeight: sortKey === 'manutencao' ? 700 : undefined,
                  }}
                >
                  {formatBRL(row.manutencao)}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    display: { xs: 'none', lg: 'table-cell' },
                    fontWeight: sortKey === 'pecas' ? 700 : undefined,
                  }}
                >
                  {formatBRL(row.pecas)}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    display: { xs: 'none', lg: 'table-cell' },
                    fontWeight: sortKey === 'multas' ? 700 : undefined,
                  }}
                >
                  {formatBRL(row.multas)}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    display: { xs: 'none', lg: 'table-cell' },
                    fontWeight: sortKey === 'ipva' ? 700 : undefined,
                  }}
                >
                  {formatBRL(row.ipva)}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    display: { xs: 'none', lg: 'table-cell' },
                    fontWeight: sortKey === 'outros' ? 700 : undefined,
                  }}
                >
                  {formatBRL(row.outros)}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

RankingTable.propTypes = {
  ranking: PropTypes.arrayOf(PropTypes.object),
  metricTotal: PropTypes.number,
  sortKey: PropTypes.string,
  loading: PropTypes.bool,
  onSelectPlaca: PropTypes.func,
};

RankingTable.defaultProps = {
  ranking: [],
  metricTotal: 0,
  sortKey: 'total',
  loading: false,
  onSelectPlaca: null,
};

const SegmentComparisonBlock = ({
  segmentItems,
  loading,
  onSelectSegment,
}) => {
  const [focusChart, setFocusChart] = useState(false);
  const [sortKey, setSortKey] = useState('total');
  const hasContent = loading || (segmentItems || []).length > 0;
  const sortedItems = useMemo(() => sortRankingBy(segmentItems, sortKey), [segmentItems, sortKey]);
  const metricTotal = useMemo(
    () => sortedItems.reduce((acc, row) => acc + getRankingSortValue(row, sortKey), 0),
    [sortedItems, sortKey],
  );
  const sortLabel = useMemo(
    () => RANKING_SORT_OPTIONS.find((o) => o.key === sortKey)?.label || 'Total',
    [sortKey],
  );

  if (!hasContent) return null;

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2, pr: 2 }}>
        <CompareArrowsOutlinedIcon fontSize="small" color="action" />
        <Typography variant="h6">Comparativo de Custos por Segmento</Typography>
      </Stack>
      <Box sx={{ mb: 2 }}>
        <RankingSortToggle sortKey={sortKey} onChange={setSortKey} />
      </Box>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, lg: 7 }}>
          <ChartCard title="Custos por segmento" onExpand={() => setFocusChart(true)}>
            <SegmentComparisonChart
              items={sortedItems}
              loading={loading}
              onSelectSegment={onSelectSegment}
              sortKey={sortKey}
            />
          </ChartCard>
        </Grid>
        <Grid size={{ xs: 12, lg: 5 }}>
          <ChartCard title="Detalhamento">
            <SegmentComparisonTable
              items={sortedItems}
              metricTotal={metricTotal}
              sortKey={sortKey}
              sortLabel={sortLabel}
              loading={loading}
              onSelectSegment={onSelectSegment}
            />
          </ChartCard>
        </Grid>
      </Grid>

      <ChartFocusDialog
        open={focusChart}
        title="Comparativo de Custos por Segmento"
        onClose={() => setFocusChart(false)}
      >
        <SegmentComparisonChart
          items={sortedItems}
          loading={loading}
          height={FOCUS_CHART_HEIGHT}
          onSelectSegment={onSelectSegment}
          sortKey={sortKey}
        />
      </ChartFocusDialog>
    </Box>
  );
};

SegmentComparisonBlock.propTypes = {
  segmentItems: PropTypes.arrayOf(PropTypes.object),
  loading: PropTypes.bool,
  onSelectSegment: PropTypes.func,
};

SegmentComparisonBlock.defaultProps = {
  segmentItems: [],
  loading: false,
  onSelectSegment: null,
};

const VehicleRankingBlock = ({
  ranking,
  loading,
  onSelectPlaca,
}) => {
  const [focusChart, setFocusChart] = useState(false);
  const [sortKey, setSortKey] = useState('total');
  const hasContent = loading || (ranking || []).length > 0;

  if (!hasContent) return null;

  const sortedRanking = useMemo(() => sortRankingBy(ranking, sortKey), [ranking, sortKey]);
  const metricTotal = useMemo(
    () => sortedRanking.reduce((acc, row) => acc + getRankingSortValue(row, sortKey), 0),
    [sortedRanking, sortKey],
  );
  const sortLabel = useMemo(
    () => RANKING_SORT_OPTIONS.find((o) => o.key === sortKey)?.label || 'Total',
    [sortKey],
  );
  const paretoInsight = useMemo(
    () => computeParetoInsight(ranking, 0, sortKey),
    [ranking, sortKey],
  );

  const handleSelectPlaca = (placa) => {
    const row = sortedRanking.find((r) => r.placa === placa);
    onSelectPlaca?.(placa, row?.veiculoId);
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2, pr: 2 }}>
        <LeaderboardOutlinedIcon fontSize="small" color="action" />
        <Typography variant="h6">Ranking de Custos por Veículo</Typography>
        <Typography variant="body2" color="text.secondary">
          — Top {RANKING_TOP_N}
        </Typography>
      </Stack>
      <Box sx={{ mb: 2 }}>
        <RankingSortToggle sortKey={sortKey} onChange={setSortKey} />
      </Box>

      <Stack spacing={2}>
        <ParetoInsight
          insight={paretoInsight}
          loading={loading}
          sortKey={sortKey}
          sortLabel={sortLabel}
        />
        <Top3Podium
          ranking={sortedRanking}
          metricTotal={metricTotal}
          sortKey={sortKey}
          sortLabel={sortLabel}
          loading={loading}
          onSelectPlaca={onSelectPlaca}
        />

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, lg: 7 }}>
            <ChartCard title="Custos por veículo" onExpand={() => setFocusChart(true)}>
              <VeiculoRankingChart
                ranking={sortedRanking}
                loading={loading}
                onSelectPlaca={handleSelectPlaca}
                sortKey={sortKey}
              />
            </ChartCard>
          </Grid>
          <Grid size={{ xs: 12, lg: 5 }}>
            <ChartCard title="Detalhamento">
              <RankingTable
                ranking={sortedRanking}
                metricTotal={metricTotal}
                sortKey={sortKey}
                loading={loading}
                onSelectPlaca={onSelectPlaca}
              />
            </ChartCard>
          </Grid>
        </Grid>
      </Stack>

      <ChartFocusDialog
        open={focusChart}
        title="Ranking de Custos por Veículo"
        onClose={() => setFocusChart(false)}
      >
        <VeiculoRankingChart
          ranking={sortedRanking}
          loading={loading}
          height={FOCUS_CHART_HEIGHT}
          onSelectPlaca={handleSelectPlaca}
          sortKey={sortKey}
        />
      </ChartFocusDialog>
    </Box>
  );
};

VehicleRankingBlock.propTypes = {
  ranking: PropTypes.arrayOf(PropTypes.object),
  loading: PropTypes.bool,
  onSelectPlaca: PropTypes.func,
};

VehicleRankingBlock.defaultProps = {
  ranking: [],
  loading: false,
  onSelectPlaca: null,
};

const VeiculoRankingSection = ({
  showSegmentComparison,
  showVehicleRanking,
  ranking,
  segmentItems,
  loading,
  onSelectPlaca,
  onSelectSegment,
}) => {
  const hasSegmentBlock = showSegmentComparison && (loading || (segmentItems || []).length > 0);
  const hasRankingBlock = showVehicleRanking && (loading || (ranking || []).length > 0);

  if (!hasSegmentBlock && !hasRankingBlock) return null;

  return (
    <Stack spacing={4}>
      {hasSegmentBlock && (
        <SegmentComparisonBlock
          segmentItems={segmentItems}
          loading={loading}
          onSelectSegment={onSelectSegment}
        />
      )}
      {hasRankingBlock && (
        <VehicleRankingBlock
          ranking={ranking}
          loading={loading}
          onSelectPlaca={onSelectPlaca}
        />
      )}
    </Stack>
  );
};

VeiculoRankingSection.propTypes = {
  showSegmentComparison: PropTypes.bool,
  showVehicleRanking: PropTypes.bool,
  ranking: PropTypes.arrayOf(PropTypes.object),
  segmentItems: PropTypes.arrayOf(PropTypes.object),
  loading: PropTypes.bool,
  onSelectPlaca: PropTypes.func,
  onSelectSegment: PropTypes.func,
};

VeiculoRankingSection.defaultProps = {
  showSegmentComparison: false,
  showVehicleRanking: true,
  ranking: [],
  segmentItems: [],
  loading: false,
  onSelectPlaca: null,
  onSelectSegment: null,
};

export default VeiculoRankingSection;
